import React, { FC, useMemo, useState } from "react";
import { Field, Input } from "@fluentui/react-components";
import DynamicDatePicker from "../../../components/ControlledDatePicker/dynamicDatepicker";
import OverlayLoader from "../../../components/OverlayLoader";
import ASCButton from "../../../components/Button";

type ValuePredictionProps = {
  selectedItem: any; // selected row from conditionExtracts
  onCancel: () => void;
  onApply: (encoded: string) => void;
};

const ValuePrediction: FC<ValuePredictionProps> = ({
  selectedItem,
  onCancel,
  onApply,
}) => {
  const [loading] = useState(false);

  const parsedCondition = useMemo(() => {
    if (!selectedItem?.Condition) return null;
    try {
      return JSON.parse(selectedItem.Condition);
    } catch {
      return null;
    }
  }, [selectedItem]);

  const tableOneNeedsValue =
    parsedCondition?.tableOne &&
    (!parsedCondition.tableOne.fieldValue ||
      parsedCondition.tableOne.fieldValue === "");

  const tableTwoNeedsValue =
    parsedCondition?.tableTwo &&
    (!parsedCondition.tableTwo.fieldValue ||
      parsedCondition.tableTwo.fieldValue === "");

  const [valueOne, setValueOne] = useState<string | null>(null);
  const [valueTwo, setValueTwo] = useState<string | null>(null);

  const handleApply = () => {
    if (!parsedCondition) return;

    const updated = { ...parsedCondition };

    if (tableOneNeedsValue && valueOne) {
      updated.tableOne.fieldValue = valueOne;
    }

    if (tableTwoNeedsValue && valueTwo) {
      updated.tableTwo.fieldValue = valueTwo;
    }

    const encoded = encodeURIComponent(JSON.stringify(updated));

    onApply(encoded);
  };

  return (
    <>
      <OverlayLoader isLoading={loading} />

      <div style={{ minHeight: 220 }}>
        {tableOneNeedsValue && (
          <Field label={`Enter value for ${parsedCondition.tableOne.label}`}>
            {parsedCondition.tableOne.fieldType === "timestamp" ? (
              <DynamicDatePicker
                name="date"
                label={null}
                required={false}
                disabled={false}
                formik={{
                  values: {
                    date: valueOne ? new Date(valueOne) : null,
                  },
                  setFieldValue: (_: any, val: Date | null) =>
                    setValueOne(val ? val.toISOString() : null),
                  setFieldTouched: () => {},
                  setFieldError: () => {},
                  errors: {},
                  touched: {},
                }}
              />
            ) : (
              <Input onChange={(e) => setValueOne(e.target.value)} />
            )}
          </Field>
        )}

        {tableTwoNeedsValue && (
          <Field label={`Enter value for ${parsedCondition.tableTwo.label}`}>
            {parsedCondition.tableTwo.fieldType === "timestamp" ? (
              <DynamicDatePicker
                name="dateTwo"
                label={null}
                required={false}
                disabled={false}
                formik={{
                  values: {
                    dateTwo: valueTwo ? new Date(valueTwo) : null,
                  },
                  setFieldValue: (_: any, val: Date | null) =>
                    setValueTwo(val ? val.toISOString() : null),
                  setFieldTouched: () => {},
                  setFieldError: () => {},
                  errors: {},
                  touched: {},
                }}
              />
            ) : (
              <Input onChange={(e) => setValueTwo(e.target.value)} />
            )}
          </Field>
        )}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
          marginTop: 20,
        }}
      >
        <ASCButton
          appearance="secondary"
          shape="rounded"
          onClick={onCancel}
          label="Cancel"
        />
        <ASCButton
          appearance="primary"
          shape="rounded"
          onClick={handleApply}
          disabled={
            (tableOneNeedsValue && !valueOne) ||
            (tableTwoNeedsValue && !valueTwo)
          }
          label="Apply"
        />
      </div>
    </>
  );
};

export default ValuePrediction;
